import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, getDoc, doc, setDoc, deleteDoc, onSnapshot, writeBatch, where } from 'firebase/firestore';
import { db } from '../firebase';
import HeaderActionsPortal from './HeaderActionsPortal';
import '../theme-palette.css';
import { Search, Download, Upload, Plus, Trash2, Edit2, Users, LayoutDashboard, ChevronDown, X } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import Papa from 'papaparse';
import { currentUserInitial as globalInitial } from '../globalState';
import { formatDateTime } from '../utils/dateFormatter';
import { useT } from '../i18n.jsx';

const TeachingDuty = () => {
  const { t } = useT();
  // Head / Deputy / Member are stored on the record and read by Admin Duty, My Duties and
  // syncExamScopeApprovers, so only the shown word changes.
  const roleWord = (v) => t(
    `dutyRoles.${String(v || '').toLowerCase().replace(/[^a-z]/g, '')}`, {}, v,
  );
  const { activeSchoolId, currentUserRole, currentUserEmail, isMobile, closeSidebar } = useOutletContext();
  // The assignment form used to sit permanently above the table, pushing it down the page. It is a
  // dialog now, opened from the tab-bar actions or by editing a row.
  const [showAssignForm, setShowAssignForm] = useState(false);
  
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Data State
  const [academicYears, setAcademicYears] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [staffMap, setStaffMap] = useState({}); // { initial: { name, email, id } }
  const [teachingDuties, setTeachingDuties] = useState([]);
  
  // Filter State
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  // Form State
  const [assignmentForm, setAssignmentForm] = useState({
    initial: '',
    department: '',
    role: 'Member'
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (activeSchoolId) {
      fetchInitialData();
    }
  }, [activeSchoolId]);

  useEffect(() => {
    const fetchDepartments = async () => {
      let departmentsData = [];
      try {
        // Check local override first
        if (activeSchoolId) {
          const localSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings'));
          if (localSnap.exists() && localSnap.data().dropdowns && Array.isArray(localSnap.data().dropdowns.departments)) {
            departmentsData = localSnap.data().dropdowns.departments;
          }
        }
        
        // Fallback to global
        if (!Array.isArray(departmentsData) || departmentsData.length === 0) {
          const globalSnap = await getDoc(doc(db, 'metadata', 'system_settings'));
          if (globalSnap.exists() && globalSnap.data().dropdowns && Array.isArray(globalSnap.data().dropdowns.departments)) {
            departmentsData = globalSnap.data().dropdowns.departments;
          }
        }
      } catch (err) {
        console.error("Error fetching departments:", err);
      }
      
      // Hard fallback
      if (!Array.isArray(departmentsData) || departmentsData.length === 0) {
        departmentsData = [
          { code: 'CHIN', name: 'Chinese Language', description: 'Chinese Language Department' },
          { code: 'ENG', name: 'English Language', description: 'English Language Department' },
          { code: 'MATH', name: 'Mathematics', description: 'Mathematics Department' }
        ];
      }
      
      // Resilient mapping in case DB has different object keys or strings
      const mappedDepartments = departmentsData.map(c => {
        if (typeof c === 'string') return { code: c, name: c };
        return {
          code: c.code || c.id || c.name || String(Math.random()),
          name: c.name || c.title || c.code || c.id || 'Unknown Department',
          active: c.active
        };
      }).filter(c => c.active !== false);
      
      setDepartments(mappedDepartments);
    };
    
    if (activeSchoolId) {
      fetchDepartments();
    }
  }, [activeSchoolId]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      // 1. Fetch Academic Years
      const ySnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'academic_years')));
      const yList = [];
      ySnap.forEach(d => yList.push({ uid: d.id, ...d.data() }));
      yList.sort((a, b) => a.yearId.localeCompare(b.yearId));
      setAcademicYears(yList);
      
      if (yList.length > 0 && !selectedYear) {
        const currentYear = yList.find(y => y.cur_year) || yList[yList.length - 1];
        setSelectedYear(currentYear.yearId);
      }

      // 2. Fetch Staff Mapping
      const staffSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'staff_profiles')));
      
      const sMap = {};
      staffSnap.forEach(doc => {
        const d = doc.data();
        if (d.initial) {
          let name = doc.id;
          if (d.firstName || d.lastName) {
            name = `${d.lastName || ''} ${d.firstName || ''}`.trim();
          }
          sMap[d.initial] = {
            id: doc.id,
            initial: d.initial,
            name: name,
            email: d.email || doc.id
          };
        }
      });
      setStaffMap(sMap);

      // 3. Listen to Teaching Duty Collection
      const dutyRef = collection(db, 'schools', activeSchoolId, 'teach_duty');
      onSnapshot(dutyRef, (snap) => {
        const duties = [];
        snap.forEach(d => {
          duties.push({ uid: d.id, ...d.data() });
        });
        setTeachingDuties(duties);
        setLoading(false);
      });

    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const filteredDuties = teachingDuties.filter(d => {
    if (selectedYear && d.sch_yr !== selectedYear) return false;
    if (selectedDepartment !== 'all' && d.department !== selectedDepartment) return false;
    return true;
  });

  const getDepartmentName = (code) => {
    const c = departments.find(x => x.code === code);
    return c ? c.name : code;
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!assignmentForm.initial || !assignmentForm.department || !selectedYear) return;
    
    try {
      const docRef = editingId ? doc(db, 'schools', activeSchoolId, 'teach_duty', editingId) : doc(collection(db, 'schools', activeSchoolId, 'teach_duty'));
      const newDuty = {
        sch_yr: selectedYear,
        department: assignmentForm.department,
        initial: assignmentForm.initial,
        role: assignmentForm.role,
        last_update_at: formatDateTime(new Date()),
        last_update_by: globalInitial || 'SYSTEM'
      };
      
      await setDoc(docRef, newDuty);
      setAssignmentForm({ ...assignmentForm, initial: '' }); // reset initial but keep department and role for faster entry
      setEditingId(null);
      setShowAssignForm(false);
    } catch (err) {
      alert(t(editingId ? 'duty.updateFailed' : 'duty.assignFailed', { message: err.message }));
    }
  };

  const handleEdit = (duty) => {
    setAssignmentForm({
      initial: duty.initial,
      department: duty.department,
      role: duty.role
    });
    setEditingId(duty.uid);
    setShowAssignForm(true);
  };

  const closeAssignForm = () => {
    setShowAssignForm(false);
    handleCancelEdit();
  };

  // Shared dialog behaviour: retract the mobile sidebar (a fixed overlay that would otherwise sit on
  // top of the sheet), stop the page behind from scrolling, and close on Escape.
  useEffect(() => {
    if (!showAssignForm) return;
    if (isMobile) closeSidebar?.();

    const onKey = (e) => { if (e.key === 'Escape') closeAssignForm(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [showAssignForm, isMobile, closeSidebar]);

  const handleCancelEdit = () => {
    setAssignmentForm({ ...assignmentForm, initial: '' });
    setEditingId(null);
  };

  const handleDelete = async (uid) => {
    if (!window.confirm(t('duty.deleteConfirm'))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'teach_duty', uid));
    } catch (err) {
      alert(t('duty.deleteFailed', { message: err.message }));
    }
  };

  const handleExportCsv = () => {
    const dataToExport = filteredDuties.map(d => ({
      'Record ID': d.uid,
      'School Year': d.sch_yr,
      'Department Code': d.department,
      'Staff Initial': d.initial,
      'Role': d.role,
      'Last Update At': d.last_update_at,
      'Last Update By': d.last_update_by
    }));

    if (dataToExport.length === 0) {
      alert(t('duty.nothingToExport'));
      return;
    }

    const csv = Papa.unparse(dataToExport);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Teaching_Duty_Export_${selectedYear}_${selectedDepartment === 'all' ? 'All' : selectedDepartment}.csv`;
    link.click();
  };

  const handleImportCsv = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          setLoading(true);
          const userInitialStr = globalInitial || 'SYSTEM';

          // Setup valid lists for validation
          const validYears = academicYears.map(y => y.yearId);
          const validDepartments = departments.map(c => c.code);
          const validStaff = Object.keys(staffMap);
          const validRoles = ['Head', 'Deputy', 'Member'];

          // 1. Data Validation Pass
          for (let i = 0; i < results.data.length; i++) {
            const row = results.data[i];
            // Skip completely empty rows
            if (!row['School Year'] && !row['sch_yr'] && !row['Department Code'] && !row['department'] && !row['Staff Initial'] && !row['initial']) {
              continue;
            }

            const sy = row['School Year'] || row['sch_yr'] || selectedYear;
            const dept = row['Department Code'] || row['department'];
            const ini = row['Staff Initial'] || row['initial'];
            const role = row['Role'] || row['role'] || 'Member';

            if (!validYears.includes(sy)) {
              alert(t('duty.badYear', { row: i + 1, value: sy }));
              setLoading(false);
              return;
            }
            if (!validDepartments.includes(dept)) {
              alert(t('teachingDuty.badDepartment', { row: i + 1, value: dept }));
              setLoading(false);
              return;
            }
            if (!validStaff.includes(ini)) {
              alert(t('duty.badInitial', { row: i + 1, value: ini }));
              setLoading(false);
              return;
            }
            if (!validRoles.includes(role)) {
              alert(t('duty.badRole', { row: i + 1, value: role }));
              setLoading(false);
              return;
            }
          }

          // 2. Execution Pass
          const batch = writeBatch(db);
          let count = 0;

          results.data.forEach(row => {
            if (!row['School Year'] && !row['sch_yr'] && !row['Department Code'] && !row['department'] && !row['Staff Initial'] && !row['initial']) {
              return;
            }

            const recordId = row['Record ID'] || row['uid'] || '';
            const sy = row['School Year'] || row['sch_yr'] || selectedYear;
            const dept = row['Department Code'] || row['department'];
            const ini = row['Staff Initial'] || row['initial'];
            const role = row['Role'] || row['role'] || 'Member';

            if (ini && dept && role) {
              const docRef = recordId ? doc(db, 'schools', activeSchoolId, 'teach_duty', recordId) : doc(collection(db, 'schools', activeSchoolId, 'teach_duty'));
              batch.set(docRef, {
                sch_yr: sy,
                department: dept,
                initial: ini,
                role: role,
                last_update_at: formatDateTime(new Date()),
                last_update_by: userInitialStr
              });
              count++;
            }
          });

          if (count > 0) {
            await batch.commit();
            alert(t('duty.imported', { count }));
          } else {
            alert(t('teachingDuty.noValidRows'));
          }
          setLoading(false);
          e.target.value = ''; // reset input
        } catch (err) {
          console.error(err);
          alert(t('duty.importFailed', { message: err.message }));
          setLoading(false);
        }
      },
      error: (err) => {
        alert(t('duty.parseFailed', { message: err.message }));
      }
    });
  };

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tabs & Filters */}
      <div className="theme-tabs-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '1.5rem' }}>
        <div className="theme-tabs-group">
          <button
            className={`theme-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <div className="theme-tab-icon">
              <LayoutDashboard size={20} />
            </div>
            {t('duty.tabs.overview')}
          </button>
          <button
            className={`theme-tab ${activeTab === 'assignment' ? 'active' : ''}`}
            onClick={() => setActiveTab('assignment')}
          >
            <div className="theme-tab-icon">
              <Edit2 size={20} />
            </div>
            {t('duty.tabs.assignment')}
          </button>
        </div>
        
        <HeaderActionsPortal>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <div>
            <select
              className="input-field"
              style={{ minWidth: '150px', padding: '0.375rem 2rem 0.375rem 0.75rem', height: 'auto', fontSize: '0.875rem' }}
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {academicYears.length === 0 && <option value="">{t('duty.noYears')}</option>}
              {academicYears.map(y => (
                <option key={y.uid} value={y.yearId}>{y.yearId}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              className="input-field"
              style={{ minWidth: '200px', padding: '0.375rem 2rem 0.375rem 0.75rem', height: 'auto', fontSize: '0.875rem' }}
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
            >
              <option value="all">{t('teachingDuty.allDepartments')}</option>
              {departments.map(c => (
                <option key={c.code} value={c.code}>{c.code}</option>
              ))}
            </select>
          </div>

          {activeTab === 'assignment' && (
            <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '0.5rem' }}>
              <button
                onClick={() => { handleCancelEdit(); setShowAssignForm(true); }}
                className="btn-primary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', width: 'auto', padding: '0.375rem 0.9rem', height: 'auto', fontSize: '0.875rem', whiteSpace: 'nowrap' }}
              >
                <Plus className="h-4 w-4" /> {t('duty.assign')}
              </button>
              <label className="btn-outline" title={t('duty.importCsv')} style={{ display: 'inline-flex', cursor: 'pointer', alignItems: 'center', width: 'auto', padding: '0.375rem 0.75rem', height: 'auto', fontSize: '0.875rem' }}>
                <Upload className="h-4 w-4" />
                <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImportCsv} />
              </label>
              <button
                onClick={handleExportCsv}
                className="btn-outline"
                title={t('duty.exportCsv')}
                style={{ display: 'inline-flex', alignItems: 'center', width: 'auto', padding: '0.375rem 0.75rem', height: 'auto', fontSize: '0.875rem' }}
              >
                <Download className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
        </HeaderActionsPortal>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', padding: isMobile ? '0.75rem' : '1.5rem' }}>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
            <div className="animate-spin" style={{ width: '2rem', height: '2rem', borderBottom: '2px solid var(--color-primary)', borderRadius: '50%' }}></div>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <div style={{ overflowX: 'auto', backgroundColor: 'var(--color-bg-app)', borderRadius: 'var(--radius-lg)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead style={{ backgroundColor: 'var(--color-bg-app)', position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                      <th style={{ padding: '1rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.875rem' }}>{t('duty.recordId')}</th>
                      <th style={{ padding: '1rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.875rem' }}>{t('duty.staffInitial')}</th>
                      <th style={{ padding: '1rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.875rem' }}>{t('teachingDuty.departmentCode')}</th>
                      <th style={{ padding: '1rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.875rem' }}>{t('duty.role')}</th>
                      <th style={{ padding: '1rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.875rem' }}>{t('duty.lastUpdate')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDuties.length > 0 ? filteredDuties.map((duty) => (
                      <tr key={duty.uid} style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', fontFamily: 'monospace', color: 'var(--color-text-muted)' }}>
                          {duty.uid}
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <div style={{ fontWeight: '600', color: 'var(--color-text-main)' }}>{duty.initial}</div>
                        </td>
                        <td style={{ padding: '1rem', color: 'var(--color-text-main)' }}>
                          {duty.department}
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <span style={{ 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '1rem', 
                            fontSize: '0.75rem', 
                            fontWeight: '600',
                            backgroundColor: duty.role === 'Head' ? 'var(--color-primary-light)' : (duty.role === 'Deputy' ? 'rgba(59, 130, 246, 0.1)' : 'var(--color-success-light)'),
                            color: duty.role === 'Head' ? 'var(--color-primary)' : (duty.role === 'Deputy' ? 'rgb(37, 99, 235)' : 'var(--color-success)')
                          }}>
                            {roleWord(duty.role)}
                          </span>
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                          {duty.last_update_at}<br/>
                          {t('duty.updatedBy', { initial: duty.last_update_by })}
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="5" style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Users size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
                            <p>{t('duty.noAllocations')}</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'assignment' && (
              <div>
                {showAssignForm && (
                <div
                  onClick={closeAssignForm}
                  style={{
                    position: 'fixed', inset: 0, zIndex: 120,
                    backgroundColor: 'var(--color-overlay)',
                    display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
                    padding: isMobile ? 0 : '2rem',
                  }}
                >
                <div
                  onClick={e => e.stopPropagation()}
                  style={{
                    backgroundColor: 'var(--color-bg-card)',
                    borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                    boxShadow: 'var(--shadow-lg)',
                    width: '100%', maxWidth: isMobile ? '100%' : '760px',
                    maxHeight: isMobile ? '92vh' : '90vh',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--color-text-main)', margin: 0, display: 'flex', alignItems: 'center' }}>
                      {editingId ? <Edit2 size={16} style={{ marginRight: '0.5rem' }} /> : <Plus size={16} style={{ marginRight: '0.5rem' }} />}
                      {editingId ? t('duty.editAssignment') : t('duty.manualAssignment')}
                    </h3>
                    <button type="button" onClick={closeAssignForm} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}>
                      <X size={20} />
                    </button>
                  </div>
                <form onSubmit={handleAssign} style={{ padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '1rem', alignItems: 'end' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--color-text-main)', marginBottom: '0.25rem' }}>{t('duty.staff')}</label>
                      <select
                        required
                        className="input-field"
                        value={assignmentForm.initial}
                        onChange={(e) => setAssignmentForm({...assignmentForm, initial: e.target.value})}
                      >
                        <option value="">{t('duty.selectStaff')}</option>
                        {Object.values(staffMap).sort((a,b) => a.initial.localeCompare(b.initial)).map(s => (
                          <option key={s.initial} value={s.initial}>{s.initial}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--color-text-main)', marginBottom: '0.25rem' }}>{t('teachingDuty.department')}</label>
                      <select
                        required
                        className="input-field"
                        value={assignmentForm.department}
                        onChange={(e) => setAssignmentForm({...assignmentForm, department: e.target.value})}
                      >
                        <option value="">{t('teachingDuty.selectDepartment')}</option>
                        {departments.map(c => (
                          <option key={c.code} value={c.code}>{c.code}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--color-text-main)', marginBottom: '0.25rem' }}>{t('duty.role')}</label>
                      <select
                        className="input-field"
                        value={assignmentForm.role}
                        onChange={(e) => setAssignmentForm({...assignmentForm, role: e.target.value})}
                      >
                        <option value="Head">{roleWord('Head')}</option>
                        <option value="Deputy">{roleWord('Deputy')}</option>
                        <option value="Member">{roleWord('Member')}</option>
                      </select>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button type="submit" className="btn-primary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                        {editingId ? <Edit2 size={16} style={{ marginRight: '0.5rem' }} /> : <Plus size={16} style={{ marginRight: '0.5rem' }} />}
                        {editingId ? t('duty.update') : t('duty.assign')}
                      </button>
                      {editingId && (
                        <button type="button" onClick={handleCancelEdit} className="btn-outline" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem 1rem' }}>
                          {t('common.cancel')}
                        </button>
                      )}
                    </div>
                  </div>
                </form>
                </div>
                </div>
                )}

                <div style={{ overflowX: 'auto', backgroundColor: 'var(--color-bg-app)', borderRadius: 'var(--radius-lg)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead style={{ backgroundColor: 'var(--color-bg-app)', position: 'sticky', top: 0, zIndex: 10 }}>
                      <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                        <th style={{ padding: '1rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.875rem' }}>{t('duty.recordId')}</th>
                        <th style={{ padding: '1rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.875rem' }}>{t('duty.staffInitial')}</th>
                        <th style={{ padding: '1rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.875rem' }}>{t('teachingDuty.departmentCode')}</th>
                        <th style={{ padding: '1rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.875rem' }}>{t('duty.role')}</th>
                        <th style={{ padding: '1rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.875rem', textAlign: 'right' }}>{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDuties.length > 0 ? filteredDuties.map((duty) => (
                        <tr key={duty.uid} style={{ borderBottom: '1px solid var(--color-border)' }}>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', fontFamily: 'monospace', color: 'var(--color-text-muted)' }}>
                            {duty.uid}
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <div style={{ fontWeight: '600', color: 'var(--color-text-main)' }}>{duty.initial}</div>
                          </td>
                          <td style={{ padding: '1rem', color: 'var(--color-text-main)' }}>
                            {duty.department}
                          </td>
                          <td style={{ padding: '1rem', color: 'var(--color-text-main)' }}>
                            {roleWord(duty.role)}
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>
                            <button
                              onClick={() => handleEdit(duty)}
                              className="btn-icon"
                              style={{ color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center', marginRight: '0.5rem' }}
                              title={t('duty.editAssignment')}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(duty.uid)}
                              className="btn-icon"
                              style={{ color: 'var(--color-danger)', display: 'inline-flex', alignItems: 'center' }}
                              title={t('duty.deleteAssignment')}
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                            {t('duty.noAssignments')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TeachingDuty;
